import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { AgGridReact } from "ag-grid-react";
import { withRouter } from "react-router-dom";
import { push } from "connected-react-router";
import { debounce, isEqual } from "lodash";
import { Segment, Divider, Pagination, Header, Icon, Button } from "semantic-ui-react";

import { loadCustomerList, changeCustomerGridState, saveCustomer } from "../actions";
import { setInvoiceBuilderCustomer } from "../../invoice/builder/actions";

import { CustomerGridColumns } from "../columns";
import GridPageSizePicker from "../../common/components/grid-page-size-picker";
import { TieredPricing, isFeatureActive } from "../../company/feature/helpers";

class CustomerContainerGrid extends React.Component {
    grid = null;

    UNSAFE_componentWillMount() {
        // attempt to load only if no data has been loaded before and there isn't already an api call happening right now
        if (!this.props.data.isLoading) {
            this.props.loadCustomerList();
        }
    }

    shouldComponentUpdate(prevProps) {
        // This is very risky because we're saying that UNLESS the number of customers are changed, don't refresh the grid
        // We only need this because when inline cell editing is done, refreshing the grid fails when `saveCustomer` action
        // is dispatched. so we block update here in order for the refresh work and let the edit handler refresh the grid manually
        return (
            prevProps?.data?.length !== this.props?.data?.length ||
            prevProps?.isLoading !== this.props?.isLoading ||
            prevProps?.pageSize !== this.props?.pageSize ||
            prevProps?.currentPage !== this.props?.currentPage
        );
    }

    componentDidUpdate(prevProps) {
        if (!this.grid) return;

        this.isTieredPricingEnabled(this.props.company);

        if (!isEqual(prevProps.hiddenColumns, this.props.hiddenColumns)) {
            this.syncColumns();
        }

        if (!this.props.isLoading) {
            this.grid.api.hideOverlay();
            this.resizeGrid();
        }

        if (prevProps.currentPage !== this.props.currentPage) {
            this.grid.api.paginationGoToPage(this.props.currentPage);
        }

        if (this.props.isLoading) {
            this.grid.api.showLoadingOverlay();
        }
    }

    resizeGrid = debounce(() => {
        this.grid && this.grid.api.sizeColumnsToFit();
    }, 300);

    isTieredPricingEnabled = company => {
        this.grid.columnApi.setColumnVisible(
            "product_price_tier_id",
            isFeatureActive(company, TieredPricing)
        );
    };

    onGridReady = grid => {
        this.grid = grid;
        this.syncColumns();

        if (this.props.isLoading) {
            grid.api.showLoadingOverlay();
        }

        this.isTieredPricingEnabled(this.props.company);
        this.grid.columnApi.applyColumnState({ state: this.props.sortModel, defaultState: {} });
        this.grid.api.setFilterModel(this.props.filterModel);
        this.grid.api.paginationGoToPage(this.props.currentPage);

        this.grid.api.addEventListener("cellDoubleClicked", e => {
            if (e.colDef.field === "name") {
                this.props.setInvoiceBuilderCustomer(e.data);
                this.props.history.push("/invoice/create");
            }
        });

        this.grid.api.addEventListener("sortChanged", () => {
            const colStates = this.grid.columnApi.getColumnState();
            const sortModel = colStates
                .filter(colState => colState.sort)
                .map(colState => ({
                    colId: colState.colId,
                    sort: colState.sort,
                }));

            this.props.changeCustomerGridState({
                sortModel,
            });
        });

        this.grid.api.addEventListener("filterChanged", () => {
            this.props.changeCustomerGridState({
                filterModel: this.grid.api.getFilterModel(),
            });
        });
    };

    async handleCellValueChanged(e) {
        // Don't trigger the popup is data is same and field is product_price_tier_id because we manually update the row value
        // which causes the change trigger to see the new value as the old value
        if (`${e.oldValue}` === `${e.newValue}` && e.colDef.field !== "product_price_tier_id")
            return;

        try {
            this.validateCellValue(e.colDef.field, e.newValue);
            // This is a whirlwind!!! when the id is changed, the value getter returns the corresponding name of the tier
            // so the newValue here is seen as the name, not the id. which is why we need to reverse search the id and send that to the server

            const newCustomer = { ...e.data };
            await this.props.saveCustomer(newCustomer, () => null);
        } catch (err) {
            return this.resetCellValue(e.node.id, e.colDef.field, e.oldValue);
        }
    }

    // eslint-disable-next-line no-unused-vars
    validateCellValue(field, newValue) {
        // Throwing an error here will cause the validator to fail, if all is good, return undefined
    }

    resetCellValue(nodeId, field, oldValue) {
        // Quickest way to reset the value of a row in the grid, causing it to refresh
        // This helps us reset the value of the cell to the original value if edit fails
        // We need this because ag-grid resets the value as soon as input in cell is saved
        if (!nodeId) return;

        this.grid.api.forEachNode(node => {
            if (node.id === nodeId) node.setData({ ...node.data, [field]: oldValue });
        });
    }

    syncColumns = () => {
        if (this.props.hiddenColumns)
            this.grid.columnApi.setColumnsVisible(this.props.hiddenColumns, false);

        if (this.props.shownColumns)
            this.grid.columnApi.setColumnsVisible(this.props.shownColumns, true);

        setTimeout(() => this.grid && this.grid.api.sizeColumnsToFit(), 300);
    };
    render() {
        const { data, pageSize, isLoading, changeCustomerGridState } = this.props;

        if (!isLoading && data.length < 1) {
            return (
                <Segment placeholder vertical>
                    <Header icon>
                        <Icon name="newspaper outline" />
                        You have not added any customer.
                    </Header>
                    <Button
                        positive
                        data-cy="add_customer"
                        onClick={() => this.props.push("/customer/create")}
                    >
                        Add Customer
                    </Button>
                </Segment>
            );
        }

        return (
            <>
                <div className="clear bm-10">
                    <Button
                        size="mini"
                        icon="download"
                        floated="right"
                        content="Export"
                        onClick={() =>
                            this.grid.api.exportDataAsCsv({
                                fileName: `customers_${Date.now()}`,
                                columnKeys: [
                                    "id",
                                    ...this.grid.columnApi
                                        .getAllDisplayedColumns()
                                        .map(c => c.colId)
                                        .filter(c => c !== "actions"),
                                ],
                            })
                        }
                    />
                </div>
                <Segment className="customer-grid" vertical>
                    <style>
                        {`.ag-theme-balham .ag-cell.ag-cell-inline-editing {
                            height: 200px;
                            width: 300px !important;
                        }`}
                    </style>
                    <div className="ag-theme-balham">
                        <AgGridReact
                            rowData={data}
                            rowHeight={50}
                            pagination={true}
                            paginationPageSize={this.props.pageSize}
                            domLayout="autoHeight"
                            getRowNode={p => p.id}
                            suppressPaginationPanel={true}
                            onGridReady={this.onGridReady}
                            columnDefs={CustomerGridColumns}
                            onCellValueChanged={this.handleCellValueChanged.bind(this)}
                            defaultColDef={{
                                sortable: true,
                                floatingFilter: true,
                                resizable: true,
                                suppressMenu: true,
                            }}
                        />
                    </div>

                    <div className="tp-10 clearing">
                        <GridPageSizePicker
                            button
                            compact
                            pointing="top"
                            pageSize={pageSize}
                            text={`${pageSize}/page`}
                            className="left floated basic"
                            onChange={changeCustomerGridState}
                        />
                        <Pagination
                            size="mini"
                            floated="right"
                            defaultActivePage={this.props.currentPage + 1}
                            totalPages={Math.ceil(data.length / pageSize)}
                            onPageChange={(e, data) =>
                                changeCustomerGridState({ currentPage: data.activePage - 1 })
                            }
                        />
                        <Divider hidden fitted clearing />
                    </div>
                </Segment>
            </>
        );
    }
}

const mapStateToProps = ({ customerGrid, customerList, auth }) => ({
    data: customerList.data,
    error: customerList.error,
    isLoading: customerList.isLoading,

    pageSize: customerGrid.pageSize,
    sortModel: customerGrid.sortModel,
    currentPage: customerGrid.currentPage,
    filterModel: customerGrid.filterModel,
    shownColumns: customerGrid.shownColumns,
    hiddenColumns: customerGrid.hiddenColumns,

    company: auth.user.company,
    productPriceTiers: auth.user.company?.productPriceTiers || [],
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            push,
            saveCustomer,
            loadCustomerList,
            changeCustomerGridState,
            setInvoiceBuilderCustomer,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CustomerContainerGrid));
